"use client";

import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bell,
  Home,
  ShoppingCart,
  MessageSquare,
  Tag,  
  Package,
  User,
  ChevronDown,
  ChevronRight,
  Search,
  Menu,
  X,
  LogOut,
  RotateCcw,
} from "lucide-react";
import createDonutChart from "./utils/chartUtils";
import { getDatabase, ref, onValue, get, set } from "firebase/database";
import "./Dashboard.css";
import UserOrders from "./Component/user-orders/userOrders";
import UserQueries from "./Component/userQueries";
import UserQuotations from "./Component/userQuotations";
import RaisedIssues from "./Component/raisedIssue";
import AltAlarm from "./Component/Alerts";
import BrandRates from "./Component/brandRates";
import SupplierNotifications from "./Component/notifications";
import StockAvailability from "./Component/stockAvailability";
import ProductOffers from "./Component/productOffer";
import { CountProvider } from "./Component/contexts/count-context";



const Dashboard = ({ adminView = false }) => {
  const navigate = useNavigate();
  const [dashboardNotifications, setDashboardNotifications] = useState([]);
  const [notificationCount, setNotificationCount] = useState(0);
  const [brand, setBrand] = useState(() =>
    adminView ? localStorage.getItem("adminSelectedBrand") : localStorage.getItem("supplierBrand") || "AcmeTech"
  );
  const [totalOrders, setTotalOrders] = useState(0);
  const [totalUsers, setTotalUsers] = useState(0);
  const [dispatchCount, setDispatchCount] = useState(0);
  const [queryCount, setQueryCount] = useState(0);
  const [currentView, setCurrentView] = useState("welcome");
  const [pendingIssuesCount, setPendingIssuesCount] = useState(0);
  const [query, setQuery] = useState("");
  const [filteredPages, setFilteredPages] = useState([]);
  const [note, setNote] = useState("Welcome to Altrange!");
  const [error, setError] = useState("");
  const db = getDatabase();
  const [issues, setIssues] = useState([]);
  const [expandedMenu, setExpandedMenu] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const chartsRef = useRef({});
  const desktopSearchRef = useRef(null);
  const mobileSearchRef = useRef(null);
  const mobileSearchContainerRef = useRef(null);
  const [isLandscape, setIsLandscape] = useState(false);

  // Check if device is in landscape mode
  useEffect(() => {
    const checkOrientation = () => {
      if (window.matchMedia("(orientation: landscape) and (max-width: 900px)").matches) {
        setIsLandscape(true);
      } else {
        setIsLandscape(false);
      }
    };

    checkOrientation();
    window.addEventListener("orientationchange", checkOrientation);
    window.addEventListener("resize", checkOrientation);

    return () => {
      window.removeEventListener("orientationchange", checkOrientation);
      window.removeEventListener("resize", checkOrientation);
    };
  }, []);

  // Lock orientation to portrait
  useEffect(() => {
    const lockOrientation = () => {
      if (window.screen && window.screen.orientation) {
        try {
          window.screen.orientation.lock("portrait").catch((err) => {
            console.log("Orientation lock not supported:", err);
          });
        } catch (err) {
          console.log("Orientation API not supported");
        }
      }
    };

    lockOrientation();
    window.addEventListener("orientationchange", lockOrientation);

    return () => {
      window.removeEventListener("orientationchange", lockOrientation);
    };
  }, []);

  // Listen for changeView events from notifications component
  useEffect(() => {
    const handleChangeView = (event) => {
      const { view, notificationId } = event.detail;
      setCurrentView(view);
    };

    window.addEventListener("changeView", handleChangeView);

    return () => {
      window.removeEventListener("changeView", handleChangeView);
    };
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        (desktopSearchRef.current && !desktopSearchRef.current.contains(event.target)) &&
        (mobileSearchRef.current && !mobileSearchRef.current.contains(event.target)) &&
        (mobileSearchContainerRef.current && !mobileSearchContainerRef.current.contains(event.target))
      ) {
        setFilteredPages([]);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Toggle submenu
  const toggleSubmenu = (menu) => {
    if (expandedMenu === menu) {
      setExpandedMenu(null);
    } else {
      setExpandedMenu(menu);
    }
  };

  // Toggle mobile menu
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Pages for search (updated with user-friendly names)
  const pages = [
    { name: "User Orders", path: "userOrders" },
    { name: "Stock Availability", path: "stockAvailability" },
    { name: "Raised Issues", path: "raisedIssues" },
    { name: "User Quotations", path: "userQuotations" },
    { name: "Notifications", path: "notifications" },
    { name: "User Queries", path: "userQueries" },
    { name: "Brand Rates", path: "brandRates" },
    { name: "productOffers", path: "productOffer" },
    { name: "AltAlarm", path: "altAlarm" },
  ];

  useEffect(() => {
    const fetchNote = async () => {
      const storedUserId = localStorage.getItem("userToken");

      if (!storedUserId) {
        console.error("User ID not found");
        return;
      }

      const noteRef = ref(db, `notes/${storedUserId}/note`);

      try {
        const snapshot = await get(noteRef);
        if (snapshot.exists()) {
          setNote(snapshot.val());
        } else {
          const defaultNote = "Welcome to Dashboard!";
          await set(noteRef, defaultNote);
          setNote(defaultNote);
        }
      } catch (error) {
        console.error("Error fetching note:", error);
      }
    };

    fetchNote();
  }, [brand]);

  useEffect(() => {
    if (!brand) return;

    const notificationsRef = ref(db, "supplier_notifications");
    
const updateNotifications = (snapshot) => {
  if (!snapshot.exists()) {
    setDashboardNotifications([]);
    setNotificationCount(0);
    return;
  }

  const currentSupplierId = localStorage.getItem("userToken");
  if (!currentSupplierId) {
    console.warn("No supplier ID found for notifications");
    setDashboardNotifications([]);
    setNotificationCount(0);
    return;
  }

  const data = snapshot.val();
  const allNotifications = [];
  let unseenCount = 0;

  Object.entries(data).forEach(([categoryKey, categoryData]) => {
    if (!categoryData || typeof categoryData !== "object") return;

    const userBucket = categoryData[currentSupplierId];
    if (!userBucket || typeof userBucket !== "object") return;

    Object.entries(userBucket).forEach(([notifId, notif]) => {
      // STRICT object check
      if (!notif || typeof notif !== "object") return;

      // STRICT brand match (same as notifications page)
      if (
        notif.brand &&
        notif.brand.trim().toLowerCase() !== brand.trim().toLowerCase()
      ) {
        return;
      }

      const item = {
        id: notifId,
        category: categoryKey,
        userId: currentSupplierId,
        ...notif,
        timestamp: notif.timestamp || new Date().toISOString(),
      };

      allNotifications.push(item);

      // 🔴 SINGLE SOURCE OF TRUTH FOR COUNT
      if (notif.seen === false || notif.seen === undefined) {
        unseenCount++;
      }
    });
  });

  console.log("Dashboard notifications:", allNotifications);
  console.log("Dashboard unseen count:", unseenCount);

  setDashboardNotifications(allNotifications);
  setNotificationCount(unseenCount);
};

    const unsubscribe = onValue(
      notificationsRef,
      updateNotifications,
      (error) => {
        console.error("Error fetching notifications:", error);
        setDashboardNotifications([]);
        setNotificationCount(0);
      }
    );

    return () => {
      unsubscribe();
      console.log("Cleaning up notifications listener...");
    };
  }, [brand, db]);


  const fetchOrders = () => {
    const ordersRef = ref(db, "orders");

    onValue(ordersRef, (snapshot) => {
      const ordersData = snapshot.val();

      if (ordersData) {
        const allOrders = [];

        Object.keys(ordersData).forEach((userId) => {
          const userOrders = ordersData[userId];
          Object.values(userOrders).forEach((order) => {
            allOrders.push(order);
          });
        });

        const filteredOrders = allOrders.filter((order) => order.brand === brand);
        setTotalOrders(filteredOrders.length);
      } else {
        setTotalOrders(0);
      }
    });
  };

  useEffect(() => {
    fetchOrders();
  }, [brand]);

  useEffect(() => {
    const fetchTotalUsers = () => {
      const usersRef = ref(db, "profileDashboard");

      onValue(
        usersRef,
        (snapshot) => {
          if (snapshot.exists()) {
            const usersData = snapshot.val();
            const totalUsers = Object.values(usersData).filter((user) => user.createdAt).length;
            setTotalUsers(totalUsers);
          } else {
            setTotalUsers(0);
          }
        },
        (error) => {
          console.error("Error fetching users:", error);
          setTotalUsers(0);
        }
      );
    };

    fetchTotalUsers();
  }, []);

  useEffect(() => {
    const fetchDispatchOrders = () => {
      const ordersRef = ref(db, "orders");

      onValue(ordersRef, (snapshot) => {
        if (snapshot.exists()) {
          let count = 0;
          const ordersData = snapshot.val();

          Object.keys(ordersData).forEach((userId) => {
            Object.values(ordersData[userId]).forEach((order) => {
              if (order.status === "Dispatch" && order.brand?.toLowerCase() === brand?.toLowerCase()) {
                count++;
              }
            });
          });

          setDispatchCount(count);
        } else {
          setDispatchCount(0);
        }
      });
    };

    fetchDispatchOrders();
  }, [brand]);

  useEffect(() => {
    const fetchPendingIssuesCount = () => {
      try {
        const issuesRef = ref(db, "issues");

        onValue(issuesRef, (snapshot) => {
          if (snapshot.exists()) {
            const data = snapshot.val();
            let count = 0;

            Object.keys(data).forEach((uid) => {
              Object.keys(data[uid]).forEach((issueId) => {
                const issue = data[uid][issueId];
                if (
                  issue.brand &&
                  issue.brand.trim().toLowerCase() === brand.trim().toLowerCase() &&
                  issue.status === "Pending"
                ) {
                  count++;
                }
              });
            });

            setPendingIssuesCount(count);
          } else {
            setPendingIssuesCount(0);
          }
        });
      } catch (error) {
        console.error("Error fetching pending issues count:", error);
      }
    };

    fetchPendingIssuesCount();
  }, [brand]);

  useEffect(() => {
    const fetchTodayQueries = () => {
      const queriesRef = ref(db, "queries");

      onValue(queriesRef, (snapshot) => {
        if (snapshot.exists()) {
          let count = 0;
          const today = new Date();
          const pad = (n) => n.toString().padStart(2, "0");
          const todayDateFormatted = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;

          const queriesData = snapshot.val();

          Object.keys(queriesData).forEach((userId) => {
            Object.values(queriesData[userId]).forEach((query) => {
              if (query.submittedAt && query.submittedAt.startsWith(todayDateFormatted)) {
                count++;
              }
            });
          });

          setQueryCount(count);
        } else {
          setQueryCount(0);
        }
      });
    };

    fetchTodayQueries();
  }, [brand]);

  const fetchOrdersForCharts = async (brand) => {
    try {
      const ordersRef = ref(db, "orders");
      const snapshot = await get(ordersRef);

      if (snapshot.exists()) {
        const ordersData = snapshot.val();
        const allOrders = [];

        Object.keys(ordersData).forEach((userId) => {
          const userOrders = ordersData[userId];
          Object.values(userOrders).forEach((order) => {
            if (order.brand === brand) {
              allOrders.push(order);
            }
          });
        });

        return allOrders;
      }
      return [];
    } catch (error) {
      console.error("Error fetching orders for charts:", error);
      return [];
    }
  };

  useEffect(() => {
    if (currentView === "welcome") {
      const initCharts = async () => {
        try {
          Object.values(chartsRef.current).forEach((chart) => {
            if (chart) chart.destroy();
          });

          const orders = await fetchOrdersForCharts(brand);
          if (!orders.length) return;

          const totalOrders = orders.length;
          const normalizeStatus = (status) => status?.trim().toLowerCase() || "";

          const statusCounts = {
            Pending: orders.filter((order) => normalizeStatus(order.status) === "pending").length,
            Underloading: orders.filter((order) => normalizeStatus(order.status) === "underloading").length,
            Dispatch: orders.filter((order) => normalizeStatus(order.status) === "dispatch").length,
          };

          const percentages = {
            Pending: totalOrders ? ((statusCounts.Pending / totalOrders) * 100).toFixed(2) : 0,
            Underloading: totalOrders ? ((statusCounts.Underloading / totalOrders) * 100).toFixed(2) : 0,
            Dispatch: totalOrders ? ((statusCounts.Dispatch / totalOrders) * 100).toFixed(2) : 0,
          };

          const pendingCtx = document.getElementById("pendingChart")?.getContext("2d");
          const underloadingCtx = document.getElementById("underloadingChart")?.getContext("2d");
          const dispatchCtx = document.getElementById("dispatchChart")?.getContext("2d");

          if (pendingCtx) {
            createDonutChart(pendingCtx, "Pending", percentages.Pending, "#D0312D", "#333");
          }

          if (underloadingCtx) {
            createDonutChart(underloadingCtx, "Processing", percentages.Underloading, "#ED7014", "#333");
          }

          if (dispatchCtx) {
            createDonutChart(dispatchCtx, "Shipped", percentages.Dispatch, "#4caf50", "#333");
          }
        } catch (error) {
          console.error("Error initializing charts:", error);
        }
      };

      const timer = setTimeout(() => {
        initCharts();
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [currentView, brand]);

  const handleSearch = (e) => {
    const searchQuery = e.target.value.toLowerCase();
    console.log("Search Query:", searchQuery); // Debug
    setQuery(searchQuery);

    if (searchQuery) {
      const filtered = pages.filter((page) =>
        page.name.toLowerCase().includes(searchQuery) ||
        page.path.toLowerCase().includes(searchQuery)
      );
      console.log("Filtered Pages:", filtered); // Debug
      setFilteredPages(filtered);
    } else {
      setFilteredPages([]);
    }
  };

  const handleNavigate = (path) => {
    console.log("Navigating to:", path); // Debug
    setCurrentView(path);
    setQuery("");
    setFilteredPages([]);
    setIsMobileMenuOpen(false);
    setMobileMenuOpen(false);
  };

  // Debug currentView changes
  useEffect(() => {
    console.log("Current View:", currentView);
  }, [currentView]);

  return (
    <div className="dashboard-container">
      {isLandscape && (
        <div className="portrait-warning">
          <RotateCcw size={16} /> Please rotate your device to portrait mode for the best experience
        </div>
      )}

      {/* Header */}
      <header className="navbar">
        <div className="navbar-container">
          <button
            className="mobile-toggle"
            type="button"
            onClick={toggleMobileMenu}
            aria-controls="navbarContent"
            aria-expanded="false"
            aria-label="Toggle navigation"
          >
            <Menu size={24} />
          </button>

          <a
            className="navbar-brand"
            href="#"
            onClick={() => window.location.reload()}
            style={{ display: "flex", alignItems: "center" }}
          >
            <img src="/Images/logo.png" alt="Logo" style={{ height: "25px", marginRight: "8px" }} />
            Supplier Dashboard
          </a>

          <div className="desktop-search" ref={desktopSearchRef}>
            <div className="search-input-group">
              <input
                type="text"
                value={query}
                onChange={handleSearch}
                placeholder="Search..."
                className="search-input"
              />
            </div>

            {query && (
              <ul className="search-dropdown">
                {filteredPages.length > 0 ? (
                  filteredPages.map((page) => (
                    <li key={page.path}>
                      <button className="dropdown-item" onClick={() => handleNavigate(page.path)}>
                        {page.name}
                      </button>
                    </li>
                  ))
                ) : (
                  <li>
                    <span className="dropdown-item-disabled">No results found</span>
                  </li>
                )}
              </ul>
            )}
          </div>

          <div className="navbar-actions">
            <div className="icon-wrapper notification-wrapper" onClick={() => handleNavigate("notifications")}>
              <button className="notification-btn">
                <Bell size={24} />
                {notificationCount > 0 && (
                  <span className="notification-badge">
                    {notificationCount}
                    <span className="sr-only">unread notifications</span>
                  </span>
                )}
              </button>
              <span className="action-label">Notifications</span>
            </div>

            <div className="icon-wrapper profile-wrapper" onClick={() => navigate("/profile")}>
              <div className="profile-image-container">
                <img
                  src="/Images/profile.jpg"
                  alt="Supplier Profile"
                  className="profile-image"
                  width="35"
                  height="35"
                />
              </div>
              <span className="action-label">Profile</span>
            </div>
          </div>
        </div>

        <div className="mobile-search" ref={mobileSearchRef}>
          <div className="search-input-group">
            <input type="text" value={query} onChange={handleSearch} placeholder="Search..." className="search-input" />
          </div>

          {query && (
            <ul className="search-dropdown">
              {filteredPages.length > 0 ? (
                filteredPages.map((page) => (
                  <li key={page.path}>
                    <button className="dropdown-item" onClick={() => handleNavigate(page.path)}>
                      {page.name}
                    </button>
                  </li>
                ))
              ) : (
                <li>
                  <span className="dropdown-item-disabled">No results found</span>
                </li>
              )}
            </ul>
          )}
        </div>
      </header>

      <div className="dashboard-main">
        <div className={`sidebar-overlay ${isMobileMenuOpen ? "show" : ""}`} onClick={toggleMobileMenu}></div>

        <aside className={`sidebar ${isMobileMenuOpen ? "show" : ""}`}>
          <div className="sidebar-header">
            <h5>Menu</h5>
            <button className="close-btn" onClick={toggleMobileMenu}>
              <X size={30} />
            </button>
          </div>

          <div className="sidebar-menu">
            <div className="menu-item" onClick={() => handleNavigate("welcome")}>
              <div className={`menu-link ${currentView === "welcome" ? "active" : ""}`}>
                <Home size={18} />
                <span>Dashboard</span>
              </div>
            </div>

            <div className="menu-group">
              <div
                className={`menu-item ${expandedMenu === "orders" ? "expanded" : ""}`}
                onClick={() => toggleSubmenu("orders")}
              >
                <div className="menu-link">
                  <ShoppingCart size={18} />
                  <span>Orders</span>
                  {expandedMenu === "orders" ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                </div>
              </div>

              {expandedMenu === "orders" && (
                <div className="submenu">
                  <div
                    className={`submenu-item ${currentView === "userOrders" ? "active" : ""}`}
                    onClick={() => handleNavigate("userOrders")}
                  >
                    <span>User Orders</span>
                  </div>
                  <div
                    className={`submenu-item ${currentView === "stockAvailability" ? "active" : ""}`}
                    onClick={() => handleNavigate("stockAvailability")}
                  >
                    <span>Stock Availability</span>
                  </div>
                </div>
              )}
            </div>

            <div className="menu-group">
              <div
                className={`menu-item ${expandedMenu === "communication" ? "expanded" : ""}`}
                onClick={() => toggleSubmenu("communication")}
              >
                <div className="menu-link">
                  <MessageSquare size={18} />
                  <span>Communication</span>
                  {expandedMenu === "communication" ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                </div>
              </div>

              {expandedMenu === "communication" && (
                <div className="submenu">
                  <div
                    className={`submenu-item ${currentView === "userQueries" ? "active" : ""}`}
                    onClick={() => handleNavigate("userQueries")}
                  >
                    <span>User Queries</span>
                  </div>
                  <div
                    className={`submenu-item ${currentView === "userQuotations" ? "active" : ""}`}
                    onClick={() => handleNavigate("userQuotations")}
                  >
                    <span>User Quotations</span>
                  </div>
                </div>
              )}
            </div>

            <div className="menu-group">
              <div
                className={`menu-item ${expandedMenu === "marketing" ? "expanded" : ""}`}
                onClick={() => toggleSubmenu("marketing")}
              >
                <div className="menu-link">
                  <Tag size={18} />
                  <span>Marketing</span>
                  {expandedMenu === "marketing" ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                </div>
              </div>

              {expandedMenu === "marketing" && (
                <div className="submenu">
                  <div
                    className={`submenu-item ${currentView === "productOffer" ? "active" : ""}`}
                    onClick={() => handleNavigate("productOffer")}
                  >
                    <span>Product Offers</span>
                  </div>
                  <div
                    className={`submenu-item ${currentView === "brandRates" ? "active" : ""}`}
                    onClick={() => handleNavigate("brandRates")}
                  >
                    <span>Brand Rates</span>
                  </div>
                </div>
              )}
            </div>

            <div className="menu-group">
              <div
                className={`menu-item ${expandedMenu === "alerts" ? "expanded" : ""}`}
                onClick={() => toggleSubmenu("alerts")}
              >
                <div className="menu-link">
                  <Bell size={18} />
                  <span>Alerts</span>
                  {expandedMenu === "alerts" ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                </div>
              </div>

              {expandedMenu === "alerts" && (
                <div className="submenu">
                  <div
                    className={`submenu-item ${currentView === "altAlarm" ? "active" : ""}`}
                    onClick={() => handleNavigate("altAlarm")}
                  >
                    <span>Alerts</span>
                  </div>
                  <div
                    className={`submenu-item badge-container ${currentView === "raisedIssues" ? "active" : ""}`}
                    onClick={() => handleNavigate("raisedIssues")}
                  >
                    <span>Raised Issues</span>
                    {pendingIssuesCount > 0 && <span className="badge">{pendingIssuesCount}</span>}
                  </div>
                </div>
              )}
            </div>
          </div>
        </aside>

        <section className="main-content">
          <CountProvider>
            <div className="content-container">
              <div className="mobile-search-container" ref={mobileSearchContainerRef}>
                <div className="search-input-group">
                  <input
                    type="text"
                    value={query}
                    onChange={handleSearch}
                    placeholder="Search..."
                    className="search-input"
                  />
                </div>

                {query && (
                  <ul className="search-dropdown">
                    {filteredPages.length > 0 ? (
                      filteredPages.map((page) => (
                        <li key={page.path} onClick={() => handleNavigate(page.path)}>
                          {page.name}
                        </li>
                      ))
                    ) : (
                      <li className="no-results">No results found</li>
                    )}
                  </ul>
                )}
              </div>

              {currentView === "welcome" && (
                <>
                  <div className="welcome-header">
                    <div className="welcome-alert">
                      <h4>{note}</h4>
                    </div>
                  </div>

                  <div className="dashboard-stats">
                    <div className="stats-grid">
                      <div className="stat-card">
                        <div className="stat-icon bg-primary-light">
                          <ShoppingCart size={24} />
                        </div>
                        <div className="stat-content">
                          <h3>{totalOrders}</h3>
                          <p>Total Sales</p>
                        </div>
                      </div>

                      <div className="stat-card">
                        <div className="stat-icon bg-success-light">
                          <Package size={24} />
                        </div>
                        <div className="stat-content">
                          <h3>{dispatchCount}</h3>
                          <p>Orders Shipped</p>
                        </div>
                      </div>

                      <div className="stat-card">
                        <div className="stat-icon bg-warning-light">
                          <User size={24} />
                        </div>
                        <div className="stat-content">
                          <h3>{totalUsers}</h3>
                          <p>Total Users</p>
                        </div>
                      </div>

                      <div className="stat-card">
                        <div className="stat-icon bg-info-light">
                          <MessageSquare size={24} />
                        </div>
                        <div className="stat-content">
                          <h3>{queryCount}</h3>
                          <p>Queries Today</p>
                        </div>
                      </div>
                    </div>

                    <div className="order-status-container">
                      <div className="order-status-card">
                        <div className="order-status-header">
                          <h5 className="order-status-title">Order Status</h5>
                        </div>
                        <div className="order-status-body">
                          <div className="chart-row">
                            <div className="chart-container">
                              <div className="chart-title">Pending Orders</div>
                              <canvas id="pendingChart" width="150" height="150"></canvas>
                            </div>
                            <div className="chart-container">
                              <div className="chart-title">Underloading Orders</div>
                              <canvas id="underloadingChart" width="150" height="150"></canvas>
                            </div>
                            <div className="chart-container">
                              <div className="chart-title">Dispatch Orders</div>
                              <canvas id="dispatchChart" width="150" height="150"></canvas>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
              {currentView === "userQuotations" && <UserQuotations />}
              {currentView === "userQueries" && <UserQueries />}
              {currentView === "userOrders" && <UserOrders />}
              {currentView === "altAlarm" && <AltAlarm />}
              {currentView === "raisedIssues" && <RaisedIssues />}
              {currentView === "brandRates" && <BrandRates />}
              {currentView === "stockAvailability" && <StockAvailability />}
              {currentView === "productOffer" && <ProductOffers />}
              {currentView === "notifications" && (
                <SupplierNotifications notifications={dashboardNotifications} adminView={adminView} />
              )}
            </div>
          </CountProvider>
        </section>
      </div>

      <footer className="footer">
        <hr />
        <p>© 2025 Altrange Private Limited. All rights reserved.</p>
      </footer>
    </div>
  );
};

export default Dashboard;